// Split screen: how many cameras the one sphere is drawn through, where each
// one's picture sits on the canvas, and — when it is a portrait of the room
// rather than something to steer — which way each is locked to face.
//
// What a viewer picks (SplitLayout):
//
//   1      one picture
//   2      left | right, both steerable, as split has always been
//   'all'  one locked pane per participant, each facing that person
//
// Gavin, 22 Oct: three and four used to be choices of their own, and three
// panes in a four-person room had to show slices of the room, cutting people
// in half. Now the room decides: 'all' is as many panes as there are people
// (two to four), so every pane is a face.
//
// What the controller draws (PaneLayout), 'all' resolved by the participants:
//
//   2 locked  side by side, centred
//   3 locked  two on top, one centred under  a triangle, point down
//   4 locked  two by two, centred            the four faces the old grid video showed
//
// Locked panes are not steered: a drag on a quarter-size picture is mostly a
// mis-aim, and several independent cameras is more than anyone wants to drive.

export type SplitLayout = 1 | 2 | 'all'

export const SPLIT_LAYOUTS: readonly SplitLayout[] = [1, 2, 'all']

export type PaneCount = 1 | 2 | 3 | 4

export interface PaneLayout {
  count: PaneCount
  locked: boolean
}

export const SINGLE_PANE: PaneLayout = { count: 1, locked: false }

// 'all' needs at least two people to mean anything: with one, it is the one
// view. Past four the panes are too small to read, so the first four listed.
export const ALL_VIEWS_MIN_PARTICIPANTS = 2
export const MAX_PANES = 4

export function hasAllViews(participantCount: number): boolean {
  return participantCount >= ALL_VIEWS_MIN_PARTICIPANTS
}

export function resolvePaneLayout(layout: SplitLayout, participantCount: number): PaneLayout {
  if (layout !== 'all') return { count: layout, locked: false }
  const count = Math.min(MAX_PANES, Math.max(ALL_VIEWS_MIN_PARTICIPANTS, participantCount)) as PaneCount
  return { count, locked: true }
}

// ?split= as a layout. 1, from when split was a single on/off, still means
// two; 3 and 4, from when they were layouts of their own, mean all.
export function splitLayoutFromParam(param: string | null): SplitLayout | null {
  if (param === null) return null
  if (param === 'all' || param === '3' || param === '4') return 'all'
  if (param === '1' || param === '2') return 2
  return null
}

// How the locked panes frame their people. Gavin, 21 Oct: quarters of a wide
// screen came out too wide with the people too small. The old grid video had
// 5:4 panels, centred, with the faces filling them. Both numbers are a
// per-video admin setting (stored as `four_view` on the video, from when only
// four panes had it; it now frames every locked layout); these are the
// defaults.
//
//   paneAspect  each panel's width over its height; the block of panels is
//               centred and the canvas round it left black
//   zoom        how far each panel's camera is zoomed in from the normal
//               view's starting zoom: 1 is the normal view, 2 doubles the
//               size of a face
export interface FourViewFraming {
  paneAspect: number
  zoom: number
}

export const DEFAULT_FOUR_VIEW: FourViewFraming = { paneAspect: 5 / 4, zoom: 1.5 }

// The ranges the admin offers. Narrower than 1:1 puts the faces in tall
// slots; wider than 16:9 is what Gavin asked to move away from. Past 3x one
// panel is a nose.
export const FOUR_VIEW_ASPECT_RANGE = { min: 1, max: 16 / 9 }
export const FOUR_VIEW_ZOOM_RANGE = { min: 1, max: 3 }

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

// Fills any missing or out-of-range part of a stored setting from the
// defaults, so a video saved before the setting existed frames as the
// defaults do.
export function resolveFourView(stored?: { pane_aspect?: number; zoom?: number } | null): FourViewFraming {
  const aspect = stored?.pane_aspect
  const zoom = stored?.zoom
  return {
    paneAspect: aspect && Number.isFinite(aspect)
      ? clamp(aspect, FOUR_VIEW_ASPECT_RANGE.min, FOUR_VIEW_ASPECT_RANGE.max)
      : DEFAULT_FOUR_VIEW.paneAspect,
    zoom: zoom && Number.isFinite(zoom)
      ? clamp(zoom, FOUR_VIEW_ZOOM_RANGE.min, FOUR_VIEW_ZOOM_RANGE.max)
      : DEFAULT_FOUR_VIEW.zoom,
  }
}

// A pane's rectangle in canvas pixels, origin top-left, as CSS lays it out.
export interface PaneRect {
  x: number
  y: number
  width: number
  height: number
}

// `paneAspect` shapes the locked layouts only: the largest block of panels
// that shape that fits, centred — two across in one row for two panes, two
// rows of two for three and four, the third centred under the first two.
export function paneRects(
  layout: PaneLayout,
  width: number,
  height: number,
  paneAspect = DEFAULT_FOUR_VIEW.paneAspect,
): PaneRect[] {
  if (!layout.locked) {
    if (layout.count === 1) return [{ x: 0, y: 0, width, height }]
    const halfW = Math.floor(width / 2)
    return [
      { x: 0, y: 0, width: halfW, height },
      { x: halfW, y: 0, width: width - halfW, height },
    ]
  }

  const cols = Math.min(layout.count, 2)
  const rows = layout.count > 2 ? 2 : 1
  const slotW = Math.floor(width / cols)
  const slotH = Math.floor(height / rows)
  // Bound by whichever of width and height runs out first; that side takes
  // its share exactly and the other follows the shape.
  const heightBound = slotH * paneAspect <= slotW
  const cellH = heightBound ? slotH : Math.min(slotH, Math.round(slotW / paneAspect))
  const cellW = heightBound ? Math.min(slotW, Math.round(slotH * paneAspect)) : slotW
  const x0 = Math.floor((width - cols * cellW) / 2)
  const y0 = Math.floor((height - rows * cellH) / 2)
  const rects: PaneRect[] = []
  for (let i = 0; i < layout.count; i++) {
    const row = Math.floor(i / 2)
    // A lone pane on the last row is centred under the two above it.
    const alone = row === rows - 1 && layout.count % 2 === 1 && i === layout.count - 1
    const x = alone ? x0 + Math.floor(cellW / 2) : x0 + (i % 2) * cellW
    rects.push({ x, y: y0 + row * cellH, width: cellW, height: cellH })
  }
  return rects
}

// The camera heading (the yaw rotateTo takes) that faces a participant
// standing at `position` degrees on the compass.
export function headingFor(position: number): number {
  return (((360 - position) % 360) + 360) % 360
}

// Which way each locked pane faces: one participant per pane, in the order
// they are listed, as the grid video framed them. With fewer participants
// than panes (only when 'all' is forced with ?split= on a video with fewer
// than two) the rest are spread evenly round the room from the first heading.
export function lockedHeadings(count: number, positions: number[]): number[] {
  const headings = positions.slice(0, count).map(headingFor)
  const start = headings[0] ?? 0
  for (let i = headings.length; i < count; i++) {
    headings.push((start + (i * 360) / count) % 360)
  }
  return headings
}
